import { useEffect, useCallback } from 'react';

const SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';
const SCRIPT_ID  = 'razorpay-checkout-script';
const TIMEOUT_MS = 10000;

// Module-level promise so multiple hook instances share a single load attempt.
let scriptReadyPromise = null;

const loadRazorpayScript = () => {
  if (scriptReadyPromise) return scriptReadyPromise;

  scriptReadyPromise = new Promise((resolve, reject) => {
    // Already available — resolve immediately.
    if (window.Razorpay) { resolve(); return; }

    // Inject the script tag if it isn't in the DOM yet.
    if (!document.getElementById(SCRIPT_ID)) {
      const script  = document.createElement('script');
      script.id     = SCRIPT_ID;
      script.src    = SCRIPT_URL;
      script.async  = true;
      document.body.appendChild(script);
    }

    // Poll for window.Razorpay — handles the race where the load event
    // already fired before this listener was attached.
    const deadline = Date.now() + TIMEOUT_MS;
    const interval = setInterval(() => {
      if (window.Razorpay) {
        clearInterval(interval);
        resolve();
      } else if (Date.now() > deadline) {
        clearInterval(interval);
        scriptReadyPromise = null;
        reject(new Error('Razorpay script failed to load — check your network connection'));
      }
    }, 100);
  });

  return scriptReadyPromise;
};

const useRazorpay = () => {
  useEffect(() => { loadRazorpayScript().catch(() => {}); }, []);

  const openCheckout = useCallback((options) =>
    loadRazorpayScript().then(
      () =>
        new Promise((resolve, reject) => {
          const rzp = new window.Razorpay({
            ...options,
            handler: resolve,
            modal: { ondismiss: () => reject(new Error('cancelled')) },
          });
          rzp.on('payment.failed', (response) => {
            reject(new Error(response.error?.description || 'Payment failed'));
          });
          rzp.open();
        }),
    ),
  []);

  return { openCheckout };
};

export default useRazorpay;
